import React from 'react';

interface FeatureCardProps {
  title: string;
  description: string;
  icon?: React.ReactNode | string;
  className?: string;
  hover?: boolean;
  details?: string[];
}

/**
 * Feature card for solution/service pages — neumorphic theme
 */
const FeatureCard: React.FC<FeatureCardProps> = ({
  title,
  description,
  icon,
  className = '',
  hover = true,
  details,
}) => {
  const defaultIcon = (
    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );

  return (
    <div
      className={`flex flex-col h-full rounded-neu bg-neu-bg p-6 transition-all duration-300 ease-out ${hover ? 'hover:-translate-y-1' : ''} ${className}`}
      style={{
        boxShadow: '9px 9px 16px rgb(163,177,198,0.6), -9px -9px 16px rgba(255,255,255,0.5)',
      }}
    >
      {/* Inset icon well */}
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center mb-5 text-neu-accent flex-shrink-0"
        style={{
          background: '#E0E5EC',
          boxShadow: 'inset 6px 6px 10px rgb(163,177,198,0.6), inset -6px -6px 10px rgba(255,255,255,0.5)',
        }}
      >
        {typeof icon === 'string' ? defaultIcon : (icon || defaultIcon)}
      </div>

      <h3 className="font-display text-lg font-semibold mb-2 text-neu-text">{title}</h3>
      <p className="text-neu-muted text-sm leading-relaxed">{description}</p>

      {details && details.length > 0 && (
        <ul className="mt-4 space-y-2 flex-1">
          {details.map((detail, index) => (
            <li key={index} className="flex items-start gap-2">
              <div
                className="w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5"
                style={{
                  background: '#E0E5EC',
                  boxShadow: 'inset 2px 2px 4px rgb(163,177,198,0.6), inset -2px -2px 4px rgba(255,255,255,0.5)',
                  color: '#6C63FF',
                }}
              >
                <svg className="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-sm text-neu-muted">{detail}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FeatureCard;
